export const HANDOVER_I18N_KEYS = {
  CHATTING_WITH_OPERATOR: 'interface.chattingWithOperator',
  CHATTING_WITH_BOT: 'interface.chattingWithBot',
  CONNECTING_WITH_OPERATOR: 'interface.connectingWithOperator'
}

export function isReceivedAgentMessage (message) {
  return message &&
    message.type !== 'system' &&
    message.type !== 'typing' &&
    message.direction === 'received' &&
    (message.author === 'bot' || message.author === 'operator')
}

export function getLastReceivedAuthor (messages) {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (isReceivedAgentMessage(messages[i])) return messages[i].author
  }
  return null
}

export function getHandoverI18nKey (fromAuthor, toAuthor) {
  if (toAuthor === 'operator' && fromAuthor && fromAuthor !== 'operator') {
    return HANDOVER_I18N_KEYS.CHATTING_WITH_OPERATOR
  }
  if (toAuthor === 'bot' && fromAuthor === 'operator') {
    return HANDOVER_I18N_KEYS.CHATTING_WITH_BOT
  }
  return null
}

export function createSystemMessage (id, text, handover = null) {
  return {
    id,
    author: 'system',
    direction: 'received',
    type: 'system',
    data: handover ? { text, handover } : { text }
  }
}

export function createHandoverSystemMessage ({ beforeMessageId, text, i18nKey }) {
  const handover = i18nKey === HANDOVER_I18N_KEYS.CHATTING_WITH_OPERATOR ? 'operator' : 'bot'
  return createSystemMessage(`handover-before-${beforeMessageId}`, text, handover)
}

export function hasOperatorHandoverSinceLastBot (messageList, pendingOperatorHandoverMessageId = null) {
  let lastBotIndex = -1
  for (let i = messageList.length - 1; i >= 0; i--) {
    if (isReceivedAgentMessage(messageList[i]) && messageList[i].author === 'bot') {
      lastBotIndex = i
      break
    }
  }

  for (let i = lastBotIndex + 1; i < messageList.length; i++) {
    const message = messageList[i]
    if (pendingOperatorHandoverMessageId && message.id === pendingOperatorHandoverMessageId) return true
    if (message.type === 'system' && message.data?.handover === 'operator') return true
  }

  return false
}

export function insertAuthorHandoverMessages (messages, translate) {
  const result = []
  let lastAuthor = null

  for (const message of messages) {
    if (isReceivedAgentMessage(message)) {
      const key = getHandoverI18nKey(lastAuthor, message.author)
      if (key) {
        result.push(createHandoverSystemMessage({
          beforeMessageId: message.id,
          text: translate(key),
          i18nKey: key
        }))
      }
      lastAuthor = message.author
    }
    result.push(message)
  }

  return result
}

export function getHandoverInsertBeforeMessage ({
  messageList,
  incomingMessage,
  translate,
  pendingOperatorHandoverMessageId
}) {
  if (!isReceivedAgentMessage(incomingMessage)) return null

  const key = getHandoverI18nKey(getLastReceivedAuthor(messageList), incomingMessage.author)
  if (!key) return null

  if (key === HANDOVER_I18N_KEYS.CHATTING_WITH_OPERATOR &&
    hasOperatorHandoverSinceLastBot(messageList, pendingOperatorHandoverMessageId)) {
    return { skip: true, clearPending: true }
  }

  const handoverId = `handover-before-${incomingMessage.id}`
  if (messageList.some(m => m.id === handoverId)) return { skip: true }

  return {
    message: createHandoverSystemMessage({
      beforeMessageId: incomingMessage.id,
      text: translate(key),
      i18nKey: key
    })
  }
}

export function getOperatorConnectingTransition ({
  messageList,
  pendingOperatorHandoverMessageId,
  translate,
  messageId
}) {
  if (hasOperatorHandoverSinceLastBot(messageList, pendingOperatorHandoverMessageId)) return null
  if (getLastReceivedAuthor(messageList) === 'operator') return null

  const id = messageId || `handover-call-operator-${Date.now()}`

  return {
    messageId: id,
    message: createSystemMessage(
      id,
      `${translate(HANDOVER_I18N_KEYS.CONNECTING_WITH_OPERATOR)}…`,
      'operator'
    ),
    pendingOperatorHandoverMessageId: id,
    chattingText: translate(HANDOVER_I18N_KEYS.CHATTING_WITH_OPERATOR)
  }
}

export function getHistoryBoundaryHandover ({
  loadedMessages,
  existingMessages,
  translate
}) {
  const lastLoadedAuthor = getLastReceivedAuthor(loadedMessages)
  const firstExisting = existingMessages.find(m => isReceivedAgentMessage(m))
  if (!firstExisting) return null

  const key = getHandoverI18nKey(lastLoadedAuthor, firstExisting.author)
  const handoverId = `handover-before-${firstExisting.id}`
  if (!key || existingMessages.some(m => m.id === handoverId)) return null

  return createHandoverSystemMessage({
    beforeMessageId: firstExisting.id,
    text: translate(key),
    i18nKey: key
  })
}
